// import Vue from "vue";
// import VueRouter from "vue-router";
import store from "../state/store";

import {createWebHistory, createRouter} from "vue-router";
import routes from "./routes";
import qs from 'qs'
import { stashTenantEntryLink } from '@/utils/tenant-access-prompt';
import { trackPlatformRoute } from '@/utils/platform-view';
import { sessionEnded } from "@/utils/session-state";
import { isEmbedded, embedAllows, embedHome, storageKey } from "@/utils/embed";
import { confirmLeave, leaveFinished } from "@/utils/unsaved-changes";

// import VueMeta from "vue-meta";

// Vue.use(VueRouter);

// Vue.use(VueMeta, {
//   // The component option name that vue-meta looks for meta info on.
//   keyName: "page",
// });

// a belépés a localStorage-ben is megvan: egy új fül (vagy levélből nyitott link) is belépve indul
const storedAuthenticated = () => {
    try {
        // a Globalixba ágyazott keret (#273) csak a saját, fülhöz kötött tárolóját nézi: a localStorage élesen a normál fülé
        return JSON.parse(sessionStorage.getItem(storageKey('authenticated')) || (isEmbedded() ? null : localStorage.getItem('authenticated')));
    } catch (e) {
        return null;
    }
};

const router = createRouter({
    history: createWebHistory(),
    parseQuery: (query) => {
        return qs.parse(query)
    },
    stringifyQuery(query) {
        const result = qs.stringify(query, { encodeValuesOnly: true })
        return result || ''
    },
    routes, // mode: "history",
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return {x: 0, y: 0};
        }
    },
});

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
    // Nem mentett szerkesztésből másik lapra: előbb megkérdezzük. A szűrő, a lapozás és a fül (csak a query) nem lap-váltás.
    const samePage = routeTo.matched.at(-1) === routeFrom.matched.at(-1)
        && JSON.stringify(routeTo.params) === JSON.stringify(routeFrom.params);

    if (routeFrom.matched.length && !samePage && !(await confirmLeave())) {
        return next(false);
    }

    // A Globalixba ágyazott keret (#273): csak a token menüpontja és ami belőle nyílik; a belépő oldal
    // helyett a lejárati üzenet (a Globalix nyitja újra). Intézményt váltó link a keretben nincs.
    if (isEmbedded() && routeTo.name !== 'sso-globalix-embed' && routeTo.name !== 'embed-expired') {
        const signedIn = store?.getters["auth/authenticated"] || storedAuthenticated();

        if (!signedIn || sessionEnded() || !embedHome()) {
            return next({name: 'embed-expired'});
        }

        if (routeTo.name !== 'error-404-basic' && !embedAllows(routeTo)) {
            return next(embedHome());
        }
    }

    // Másik intézmény lapjára mutató link (`?tenant=<id>`, íráshoz `&write=1`): a jog-ellenőrzés ELŐTT, mert
    // belépés nélkül a lap 404 lenne. A kérést a gyökérben ülő ablak veszi át, utána a céllap töltődik be.
    if (routeTo.query?.tenant && (store?.getters["auth/authenticated"] || storedAuthenticated())) {
        const {tenant, write, ...rest} = routeTo.query;
        const search = qs.stringify(rest, {encode: false});
        stashTenantEntryLink({tenantId: Number(tenant), withWrite: write === '1', target: routeTo.path + (search ? '?' + search : '')});
        next({path: '/'});
        return false;
    }

    // Platform szinten (a global admin nem lépett be) egy intézményi lap intézmény nélküli címe 404 helyett a
    // belépési ablakra visz, intézmény-választóval; belépés után a kért lap nyílik meg.
    // csak a belépésre kötelezett global adminnál kérdez: másnál a várakozás az oldal betöltését késleltetné
    const me = store?.state?.user?.authenticated || {};
    const mayNeedEntry = !!me.is_global && !!me.is_admin && !me.is_developer && !me.tenant_context?.entered;

    if (mayNeedEntry && routeTo.matched[1]?.meta?.type === 'tenant' && routeTo.path !== '/'
        && (store?.getters["auth/authenticated"] || storedAuthenticated())) {
        await store.dispatch('tenant/fetchMine').catch(() => {});

        if (store.getters['tenant/requiresEntry'] && !store.getters['tenant/current']) {
            stashTenantEntryLink({tenantId: null, withWrite: false, target: routeTo.fullPath});
            next({path: '/'});
            return false;
        }
    }

    const authRequired = routeTo.matched.some(matched => matched.meta.authRequired);
    let authenticated = store?.getters["auth/authenticated"] || storedAuthenticated();
    const can = store?.getters['auth/can'];
    let locale = store?.getters['trans/locale'];
    // A lokalizált cím a PATH-ra van kulcsolva, nem a teljes címre. A `fullPath` a
    // query stringet is tartalmazza, arra pedig sosincs alias — így minden olyan
    // képernyő ANGOL címen maradt, ahol paraméter kerül a címbe: szűrés, rendezés,
    // lapozás. A táblázat az első ilyen lépésnél átírta a magyar címet kanonikusra.
    let key = 'routes.'+routeTo.path;
    const translation = store.$t(key, {}, locale);

    // belépés nélkül (vagy lejárt munkamenettel) a jogok nem ismertek: előbb a belépő, a cím megmarad
    if (authRequired && (!authenticated || sessionEnded()) && routeTo.name !== 'error-404-basic') {
        return next({name: "login", query: {redirectFrom: routeTo.fullPath}});
    }

    if (authRequired && routeTo.name !== 'error-404-basic' && routeTo.path !== '/' && !routeTo.meta?.ownProfile) {
        const route = router.getRoutes().find((route) => route.name === routeTo.name)?.path;
        const type = routeTo.matched[1]?.meta?.type;

        // A create / edit AL-útvonalakhoz nincs saját menüpont — a menüpontok a
        // listákhoz készülnek (jelenleg 195-ből 0 db `create` és 0 db `edit`), így a
        // teljes path-ra kért jog SOSEM létezhet. Enélkül a szűkítés nélkül minden
        // create és edit űrlap 404 volt minden nem-admin felhasználónak; adminnál
        // csak azért nem, mert a verify() a fejlesztő/globális admin rövidzáron
        // átengedi. Az al-képernyő ugyanannak a képernyőnek a része, ezért a SZÜLŐ
        // (lista) menüpont jogát nézzük — azt a jogot amúgy is ki kell osztani ahhoz,
        // hogy a felhasználó egyáltalán eljusson ide.
        const candidates = [route];

        if (route) {
            const parent = route.replace(/\/(create|:id\(\\d\+\)|:id)$/, '');

            if (parent && parent !== route) {
                candidates.push(parent);
            }
        }

        if (!candidates.some((path) => can(type + '.menu_points.' + path))) {
            next({name: "error-404-basic"});
            return false;
        }
    }

    if (key !== translation) {
        // A query string a lefordított path MÖGÉ kerül vissza — enélkül a szűrő és a
        // lapozás paraméterei elvesznének a fordításkor.
        const query = routeTo.fullPath.slice(routeTo.path.length);

        routeTo.path = translation;
        routeTo.fullPath = translation + query;
        routeTo.href = translation + query;
    }

    //   // If auth isn't required for the route, just continue.
    if (!authRequired) {
        next()
    } else {
        // console.log(2)
        if (!authenticated) {
            // console.log(3)
            return next({name: "login", query: {redirectFrom: routeTo.fullPath}});
        }
        // console.log(4)
        next();
    }

    // next()

    // eslint-disable-next-line no-unused-vars
    // eslint-disable-next-line no-inner-declarations
    /*function redirectToLogin() {
        // Pass the original route to the login component
        next({name: "login", query: {redirectFrom: routeTo.fullPath}});
    }*/

});

// a már lezáruló navigáció is felülírhatja a lejárt munkamenet átirányítását: utána még egyszer a belépőre
router.afterEach((routeTo) => {
    leaveFinished();

    if (sessionEnded() && isEmbedded()) {
        if (routeTo.name !== 'embed-expired' && routeTo.name !== 'sso-globalix-embed') {
            router.replace({name: 'embed-expired'});
        }
        return;
    }
    if (sessionEnded() && routeTo.name !== 'login' && routeTo.matched.some(matched => matched.meta.authRequired)) {
        router.replace({name: 'login', query: {redirectFrom: routeTo.fullPath}});
    }
});

router.beforeResolve((routeTo) => {
    trackPlatformRoute(routeTo);
});

// router.beforeResolve(async (routeTo, routeFrom, next) => {
// Create a `beforeResolve` hook, which fires whenever
// `beforeRouteEnter` and `beforeRouteUpdate` would. This
// allows us to ensure data is fetched even when params change,
// but the resolved route does not. We put it in `meta` to
// indicate that it's a hook we created, rather than part of
// Vue Router (yet?).
// try {
//   // For each matched route...
//   for (const route of routeTo.matched) {
//     await new Promise((resolve, reject) => {
//       // If a `beforeResolve` hook is defined, call it with
//       // the same arguments as the `beforeEnter` hook.
//       if (route.meta && route.meta.beforeResolve) {
//         route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
//           // If the user chose to redirect...
//           if (args.length) {
//             // If redirecting to the same route we're coming from...
//             // Complete the redirect.
//             next(...args);
//             reject(new Error("Redirected"));
//           } else {
//             resolve();
//           }
//         });
//       } else {
//         // Otherwise, continue resolving the route.
//         resolve();
//       }
//     });
//   }
//   // If a `beforeResolve` hook chose to redirect, just return.
// } catch (error) {
//   return;
// }

// // If we reach this point, continue resolving the route.
// next();
// });

export default router;
